// Casca do HOPE, igualada à do AgentOS (spec docs/screens/18-shell.md).
// Router por hash, sidebar de 15 itens, rodapé Runner/Settings/Sign out.

import { aoAtualizar, github, ensureSession } from "./api";
import { icon } from "./icons";
import * as p from "./pages";
import { tasks } from "./pages/tasks";
import { taskForm } from "./pages/task-form";
import { taskDetail } from "./pages/task-detail";
import { goals } from "./pages/goals";
import { goalDetail } from "./pages/goal-detail";
import { goalForm } from "./pages/goal-form";
import { inbox } from "./pages/inbox";
import { sessions } from "./pages/sessions";
import { costs } from "./pages/costs";
import { skills } from "./pages/skills";
import { environments } from "./pages/environments";
import { agents } from "./pages/agents";
import { files, repos } from "./pages/files";

type Render = (el: HTMLElement) => void | Promise<void>;

/** A sidebar do vídeo: 15 itens, nesta ordem exata (18-shell.md). */
const NAV: Array<{ path: string; label: string; icon: string }> = [
  { path: "/inbox", label: "Inboxes", icon: "inbox" },
  { path: "/activity", label: "Activity", icon: "activity" },
  { path: "/tasks", label: "Tasks", icon: "tasks" },
  { path: "/goals", label: "Goals", icon: "goals" },
  { path: "/sessoes", label: "Sessions", icon: "sessions" },
  { path: "/custos", label: "Costs", icon: "costs" },
  { path: "/skills", label: "Skills", icon: "skills" },
  { path: "/ambientes", label: "Environments", icon: "environments" },
  { path: "/agentes", label: "Agents", icon: "agents" },
  { path: "/templates", label: "Templates", icon: "templates" },
  { path: "/arquivos", label: "Files", icon: "files" },
  { path: "/conhecimento", label: "Knowledge", icon: "knowledge" },
  { path: "/repos", label: "Repos", icon: "repos" },
  { path: "/conexoes", label: "Connections", icon: "connections" },
  { path: "/admin", label: "Admin", icon: "admin" },
];

const view = document.getElementById("view")!;
const sidebar = document.getElementById("sidebar")!;

const currentPath = () => location.hash.replace(/^#/, "") || "/";
const seg = (path: string, i: number) => path.split("/").filter(Boolean)[i] ?? "";

/** Resolve a rota. Sub-rotas com id numérico vão para o detalhe; palavras
 *  reservadas ("new", "archived") vão para a tela certa da mesma área. */
function resolve(path: string): { render: Render; raiz: string } {
  const raiz = "/" + seg(path, 0);
  const sub = seg(path, 1);

  switch (raiz) {
    case "/":
      location.hash = "/tasks"; // o app do vídeo abre no board
      return { render: tasks, raiz: "/tasks" };
    case "/tasks":
      if (sub === "new") return { render: taskForm, raiz };
      // Identificador do Linear (BYM-1583), não número de issue: a task deixou
      // de ser do GitHub e o segmento agora é letras-hífen-número.
      if (/^[A-Za-z][A-Za-z0-9]*-\d+$/.test(sub)) return { render: taskDetail, raiz };
      return { render: tasks, raiz };
    case "/goals":
      if (sub === "new") return { render: goalForm, raiz };
      if (/^\d+$/.test(sub)) return { render: goalDetail, raiz };
      return { render: goals, raiz };
    case "/inbox": return { render: inbox, raiz };
    case "/sessoes": return { render: sessions, raiz };
    case "/custos": return { render: costs, raiz };
    case "/skills": return { render: skills, raiz };
    case "/ambientes": return { render: environments, raiz };
    case "/agentes": return { render: agents, raiz };
    case "/arquivos": return { render: files, raiz };
    case "/repos": return { render: repos, raiz };
    case "/templates": return { render: p.templates, raiz };
    // Knowledge é o docs/ do repo — o mesmo navegador de Files, outra raiz.
    // Redireciona em vez de duplicar o navegador inteiro.
    case "/conhecimento":
      return { render: () => { location.hash = "/arquivos/docs"; }, raiz };
    case "/conexoes": return { render: p.conexoes, raiz };
    case "/admin": return { render: p.admin, raiz };
    case "/activity": return { render: p.activity, raiz };
    default: return { render: tasks, raiz: "/tasks" };
  }
}

/** Badges da sidebar, derivados como no contrato do 18-shell: Inboxes conta
 *  issues abertas cujo último comentário não é seu — "esperando você". */
async function badges(): Promise<{ projeto: string; inbox: number }> {
  const [repo, eu, comentarios] = await Promise.all([
    github.repo("by-mav/hope"),
    github.me(),
    github.comments("by-mav/hope"),
  ]);
  const ultimo = new Map<string, string>();
  for (const c of comentarios) {
    const n = c.issue_url.split("/").pop()!;
    if (!ultimo.has(n)) ultimo.set(n, c.user.login);
  }
  const esperando = [...ultimo.values()].filter((login) => login !== eu.login).length;
  return { projeto: repo.name.toUpperCase(), inbox: esperando };
}

function renderSidebar(): void {
  const ativa = resolve(currentPath()).raiz;

  sidebar.innerHTML = `
    <button class="project" type="button" aria-label="Projeto atual">
      <span class="project-mark">H</span>
      <span class="project-name" data-project>HOPE</span>
      <span class="project-count" data-open hidden></span>
      ${icon("chevron", "project-chevron")}
    </button>
    <nav class="nav" aria-label="Seções">
      ${NAV.map(
        (r) => `
        <a class="nav-item" href="#${r.path}" ${r.path === ativa ? 'aria-current="page"' : ""}>
          ${icon(r.icon)}
          <span class="nav-label">${r.label}</span>
          <span class="nav-count" data-count="${r.path}" hidden></span>
        </a>`,
      ).join("")}
    </nav>
    <div class="foot">
      <div class="foot-row">
        <span class="dot" data-state="on"></span>
        <span>Runner</span>
        <span class="foot-state">Running</span>
      </div>
      <a class="foot-row foot-link" href="#/admin">${icon("settings", "foot-icon")}<span>Settings</span></a>
      <button class="foot-row foot-link" type="button">${icon("signout", "foot-icon")}<span>Sign out</span></button>
    </div>`;

  badges()
    .then(({ projeto, inbox: n }) => {
      sidebar.querySelector<HTMLElement>("[data-project]")!.textContent = projeto;
      const el = sidebar.querySelector<HTMLElement>('[data-count="/inbox"]');
      if (el && n > 0) {
        el.textContent = String(n);
        el.hidden = false;
      }
    })
    .catch(() => {});
}

/** Geração do render corrente. Toda página busca dado de forma assíncrona; sem
 *  isto, clicar em Agents enquanto Tasks ainda carregava fazia o Tasks terminar
 *  depois e sobrescrever o Agents — a tela trocava sozinha debaixo do dedo.
 *  Quem termina fora da sua geração não escreve mais nada. */
let geracao = 0;

async function route(): Promise<void> {
  const minha = ++geracao;
  const path = currentPath();
  const { render, raiz } = resolve(path);
  renderSidebar();
  const rotulo = NAV.find((x) => x.path === raiz)?.label ?? "HOPE";
  document.title = `${rotulo} · HOPE`;

  // O palco entra na tela JÁ, vazio: é ele que vai receber o esqueleto que a
  // página pinta no primeiro passo. Antes eu só anexava no fim, e o efeito era
  // ficar olhando a tela anterior congelada até a nova terminar de carregar —
  // sem nenhum sinal de que algo estava acontecendo.
  //
  // A proteção contra corrida continua, e sai de graça: quando uma rota nova
  // começa, este `replaceChildren` desliga o palco da anterior, e o render
  // atrasado dela passa a escrever num nó fora da tela.
  const palco = document.createElement("div");
  palco.className = "palco";
  view.replaceChildren(palco);
  try {
    await render(palco);
  } finally {
    if (minha !== geracao) palco.remove();
  }
}

addEventListener("hashchange", route);

// Revalidação em segundo plano trouxe dado novo: repinta a rota atual.
aoAtualizar(() => {
  void route();
});

// Desenha JÁ. Antes o boot era `ensureSession().finally(route)`, e a sessão
// anônima do Supabase segurava a primeira pintura: em produção o host apontava
// para um túnel morto, o DNS levava ~5,8 s para falhar, e a tela ficava em
// branco esse tempo todo antes de a primeira requisição sequer sair.
//
// Nada da tela inicial depende dessa sessão — task, goal, frota e arquivo vêm
// do GitHub e do LiteLLM. Só Activity e Sessions ainda leem do Supabase, e elas
// esperam a sessão por conta própria.
void route();
void ensureSession().catch(() => {});
